import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, SlidersHorizontal, MapPin, Gauge, Zap, MessageCircle, ShoppingCart, Loader2, Car } from 'lucide-react';
import { listVehicles } from '../lib/vehicles';
import HeartButton from '../components/HeartButton';
import VerifiedBadge from '../components/VerifiedBadge';

export default function Marketplace() {
  const navigate = useNavigate();
  const [q, setQ] = React.useState('');
  const [make, setMake] = React.useState('All');
  const [maxPrice, setMaxPrice] = React.useState(500000);
  const [vehicles, setVehicles] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await listVehicles({ limit: 100 });
        if (!cancelled) setVehicles(data);
      } catch (e) {
        if (!cancelled) setVehicles([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const makes = ['All', ...Array.from(new Set(vehicles.map((v) => v.make))).sort()];

  const filtered = vehicles.filter((v) => {
    const matchesQ = q === '' || v.title.toLowerCase().includes(q.toLowerCase());
    const matchesMake = make === 'All' || v.make === make;
    const matchesPrice = v.price <= maxPrice;
    return matchesQ && matchesMake && matchesPrice;
  });

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-14" data-testid="page-marketplace">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#ff2a2a] mb-2">Marketplace</div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight leading-none">Find your next build</h1>
          <p className="mt-2 text-zinc-400 text-sm">Fair commission. No hidden fees. Verified sellers.</p>
        </div>
        <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">
          {loading ? 'Loading…' : <>Showing <span className="text-white">{filtered.length}</span> / {vehicles.length}</>}
        </div>
      </div>

      {/* Info banner — free inquiry */}
      <div className="mb-8 hairline p-4 bg-[#141414] flex flex-col sm:flex-row items-start sm:items-center gap-3" data-testid="marketplace-banner">
        <MessageCircle className="w-5 h-5 text-emerald-400 shrink-0" />
        <div className="text-sm text-zinc-300 flex-1">
          <span className="text-white font-semibold">Free inquiries on every listing.</span>{' '}
          Message any seller with zero fees. Arrange test drives, viewings, and delivery directly with the owner.
        </div>
      </div>

      {/* Filters */}
      <div className="am-card p-4 mb-8 grid md:grid-cols-12 gap-3 items-center" data-testid="marketplace-filters">
        <div className="md:col-span-5 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="filter-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search make, model, keyword…"
            className="w-full bg-[#0d0d0d] hairline pl-9 pr-3 py-2.5 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#ff2a2a]"
          />
        </div>
        <div className="md:col-span-3">
          <select
            data-testid="filter-make"
            value={make}
            onChange={(e) => setMake(e.target.value)}
            className="w-full bg-[#0d0d0d] hairline px-3 py-2.5 text-sm focus:outline-none focus:border-[#ff2a2a]"
          >
            {makes.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="md:col-span-3">
          <div className="flex items-center justify-between text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">
            <span>Max Price</span>
            <span className="text-white">${maxPrice.toLocaleString()}</span>
          </div>
          <input
            data-testid="filter-price"
            type="range"
            min="5000"
            max="500000"
            step="1000"
            value={maxPrice}
            onChange={(e) => setMaxPrice(+e.target.value)}
            className="w-full accent-[#ff2a2a]"
          />
        </div>
        <div className="md:col-span-1 flex justify-end">
          <SlidersHorizontal className="w-4 h-4 text-zinc-500" />
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid place-items-center py-24" data-testid="marketplace-loading">
          <Loader2 className="w-8 h-8 text-[#ff2a2a] animate-spin" />
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="marketplace-grid">
          {filtered.map((v) => (
            <article key={v.id} className="am-card overflow-hidden group flex flex-col relative" data-testid={`vehicle-card-${v.id}`}>
              <Link to={`/vehicles/${v.id}`} className="block relative aspect-[4/3] overflow-hidden" data-testid={`vehicle-link-${v.id}`}>
                {v.photos && v.photos[0] ? (
                  <img src={v.photos[0]} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full grid place-items-center bg-[#131313] text-zinc-600"><Car className="w-8 h-8" /></div>
                )}
                {v.tag && <div className="absolute top-3 left-3 am-pill bg-black/60 backdrop-blur-md">{v.tag}</div>}
                {v.seller_verified && (
                  <div className={`absolute ${v.tag ? 'top-11' : 'top-3'} left-3`}>
                    <VerifiedBadge compact dataTestId={`verified-${v.id}`} />
                  </div>
                )}
                <div className="absolute bottom-3 right-3 font-mono text-lg text-white bg-black/60 px-2.5 py-1 backdrop-blur-md">
                  ${v.price.toLocaleString()}
                </div>
              </Link>
              <div className="absolute top-3 right-3 z-10">
                <HeartButton vehicleId={v.id} compact dataTestId={`heart-${v.id}`} />
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <Link to={`/vehicles/${v.id}`} className="text-white font-semibold hover:text-[#ff2a2a] transition-colors">{v.title}</Link>
                <div className="mt-3 grid grid-cols-3 gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                  <div className="flex items-center gap-1"><Gauge className="w-3 h-3" />{((v.mileage || 0) / 1000).toFixed(0)}k km</div>
                  {v.hp ? <div className="flex items-center gap-1"><Zap className="w-3 h-3" />{v.hp} HP</div> : <div />}
                  <div className="flex items-center gap-1"><MapPin className="w-3 h-3" />{(v.location || '').split(',')[0]}</div>
                </div>
                <div className="mt-auto pt-4 grid grid-cols-2 gap-2">
                  <Link
                    to={`/vehicles/${v.id}#inquiry`}
                    className="hairline hover:border-[#ff2a2a] hover:text-white text-zinc-200 text-xs font-semibold py-2.5 transition-colors inline-flex items-center justify-center gap-1.5"
                    data-testid={`ask-question-${v.id}`}
                  >
                    <MessageCircle className="w-3.5 h-3.5" /> Ask <span className="text-emerald-400">free</span>
                  </Link>
                  <Link
                    to={`/vehicles/${v.id}`}
                    className="bg-[#ff2a2a] hover:bg-[#e01f1f] text-white text-xs font-semibold py-2.5 transition-colors inline-flex items-center justify-center gap-1.5"
                    data-testid={`view-vehicle-${v.id}`}
                  >
                    <ShoppingCart className="w-3.5 h-3.5" /> View
                  </Link>
                </div>
              </div>
            </article>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full am-card p-16 text-center" data-testid="marketplace-empty">
              <ShoppingCart className="w-10 h-10 mx-auto text-zinc-700" />
              <div className="mt-4 font-display text-2xl uppercase tracking-tight text-white">
                {vehicles.length === 0 ? 'No listings yet' : 'No matches for your filters'}
              </div>
              <p className="mt-2 text-zinc-400 text-sm max-w-md mx-auto">
                {vehicles.length === 0
                  ? 'Be the first to list a vehicle on AutoMobile. Zero listing fees — you only pay commission on sale.'
                  : 'Try widening your price range or clearing the search.'}
              </p>
              {vehicles.length === 0 && (
                <Link to="/sell" className="mt-6 inline-block bg-[#ff2a2a] hover:bg-[#e01f1f] text-white text-sm font-semibold px-6 py-3 transition-colors">
                  List your vehicle
                </Link>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
